import React from 'react';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import Link from '@material-ui/core/Link';
import usePageMeta from '../../hooks/usePageMeta';

const useStyles = makeStyles({
  root: {
    width: '100%',
    maxWidth: 1000,
    margin: 'auto',
    '& p':{
        textAlign: 'left',
    },
    '& ul li':{
        listStyle:'decimal',
        padding: '5px',
        textAlign: 'left',
    }
  },
});


export default function Privacy() {
    const classes = useStyles();
    usePageMeta('Privacy Policy', 'How The Warrior Tribe collects, uses, and protects your data.');

  
    return (
      <div className={classes.root}>
            <Typography variant="h3" gutterBottom>
                PRIVACY NOTICE
            </Typography>
            <Typography variant="h6" gutterBottom>
                Last updated June 1, 2026
            </Typography>
            <Typography variant="body1" gutterBottom>
                Thank you for choosing to be part of our community at The Warrior Tribe (“company”, “we”, “us”, or “our”). We are committed to protecting your personal information and your right to privacy. If you have any questions or concerns about our policy, or our practices with regards to your personal information, please contact us at support@thewarriortribe.xyz
            </Typography>
            <br/>
            <Typography variant="body1" gutterBottom>
                When you visit our website,
                <Link color="blue" href="https://www.thewarriortribe.xyz">
                    https://www.thewarriortribe.xyz
                </Link>
                , you trust us with your personal information. We take your privacy very seriously. In this privacy notice, we describe our privacy policy. We seek to explain to you in the clearest way possible what information we collect, how we use it and what rights you have in relation to it. We hope you take some time to read through it carefully, as it is important. If there are any terms in this privacy policy that you do not agree with, please discontinue use of our Sites and our services.
            </Typography>
            <br/>
            <Typography variant="body1" gutterBottom>
                This privacy policy applies to all information collected through our website
                (such as <Link color="blue" href="https://www.thewarriortribe.xyz">
                           https://www.thewarriortribe.xyz
                        </Link>),
                and/or any related services, sales, marketing or events (we refer to them collectively in this privacy policy as the "Sites").
            </Typography>
            <br/>

            <Typography variant="body1" gutterBottom>
                <b> Please read this privacy policy carefully as it will help you make informed decisions about sharing your personal information with us. </b>
            </Typography>
            <br/>

            <Typography variant="h5" gutterBottom>
                1. WHAT INFORMATION DO WE COLLECT?
            </Typography>
            <br/>
            <Typography variant="h6" gutterBottom>
                Information you provide directly
            </Typography>
            <Typography variant="body1" gutterBottom>
                <i>In Short: We collect only the minimum information needed to operate the Service.</i>
            </Typography>
            <br/>
            <Typography variant="body1" gutterBottom>
                When you use The Warrior Tribe, we collect your Google account email address (used as your unique identifier via Google Sign-In), the avatar name you choose during onboarding, and your XP and streak data generated by your use of the extension. We do not collect payment card information directly — all payments are handled by Stripe. We do not purchase or obtain data from third-party data brokers or marketing partners.
            </Typography>
            <br/>

            <Typography variant="h5" gutterBottom>
                2. HOW DO WE USE YOUR INFORMATION?
            </Typography>
            <Typography variant="body1" gutterBottom>
                <i>In Short: We use your information only to operate the Service and communicate with you about your account.</i>
            </Typography>
            <br/>
            <Typography variant="body1" gutterBottom>
                We use the information we collect to:
           </Typography>
           <br/>

            <ul>
                <li>Identify your account and maintain your XP, streak, and subscription status.</li>
                <li>Process and manage your subscription payments via Stripe.</li>
                <li>Send administrative communications about your account, subscription, or changes to our policies.</li>
                <li>Respond to your support requests and feedback.</li>
                <li>Monitor for fraud and keep the Service secure.</li>
                <li>Analyze aggregate usage trends to improve the Service (no individual-level behavioral profiling).</li>
                <li>Comply with legal obligations and respond to lawful requests.</li>
            </ul>



            <Typography variant="h5" gutterBottom>
                3. WILL YOUR INFORMATION BE SHARED WITH ANYONE?    
            </Typography>
            <Typography variant="body1" gutterBottom>
                <i>In Short: We only share information with your consent, to comply with laws, to protect your rights, or to fulfill business obligations.</i>  
            </Typography>
           <br/>

            <ul>
                <li>Consent: We may process your data if you have given us specific consent to use your personal information in a specific purpose. </li>
                <li>Legitimate Interests: We may process your data when it is reasonably necessary to achieve our legitimate business interests. </li>
                <li>Performance of a Contract: Where we have entered into a contract with you, we may process your personal information to fulfill the terms of our contract. </li>
                <li>Legal Obligations: We may disclose your information where we are legally required to do so in order to comply with applicable law, governmental requests, a judicial proceeding, court order, or legal process, such as in response to a court order or a subpoena (including in response to public authorities to meet national security or law enforcement requirements). </li>
                <li>Vital Interests: We may disclose your information where we believe it is necessary to investigate, prevent, or take action regarding potential violations of our policies, suspected fraud, situations involving potential threats to the safety of any person and illegal activities, or as evidence in litigation in which we are involved.</li>
                <li>Vendors, Consultants and Other Third-Party Service Providers. We may share your data with third party vendors, service providers, contractors or agents who perform services for us or on our behalf and require access to such information to do that work. Examples include: payment processing, data analysis, email delivery, hosting services, customer service and marketing efforts. We may allow selected third parties to use tracking technology on the Sites, which will enable them to collect data about how you interact with the Sites over time. This information may be used to, among other things, analyze and track data, determine the popularity of certain content and better understand online activity. Unless described in this Policy, we do not share, sell, rent or trade any of your information with third parties for their promotional purposes. </li>
                <li>Business Transfers. We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.  </li>
            </ul> 

            <br/>

            <Typography variant="h5" gutterBottom>
                4. WHO WILL YOUR INFORMATION BE SHARED WITH?     
            </Typography>
            <Typography variant="body1" gutterBottom>
                <i>In Short:  We only share information with the following third parties.</i>  
            </Typography>
           <br/>
           <Typography variant="body1" gutterBottom>
           We only share and disclose your information with the following third parties. We have categorized each party so that you may be easily understand the purpose of our data collection and processing practices. If we have processed your data based on your consent and you wish to revoke your consent, please contact us.
            </Typography>
            <br/>

            <ul>
                <li><strong>Stripe</strong> — payment processing and subscription billing</li>
                <li><strong>Google Analytics</strong> — aggregate web analytics</li>
                <li><strong>Google Sign-In</strong> — account authentication via the Chrome identity API</li>
            </ul>

            <br/>

            <Typography variant="h5" gutterBottom>
                5. DO WE USE COOKIES AND OTHER TRACKING TECHNOLOGIES?     
            </Typography>
            <Typography variant="body1" gutterBottom>
                <i>In Short: We may use cookies and other tracking technologies to collect and store your information.</i>  
            </Typography>
           <br/>
             <Typography variant="body1" gutterBottom>
                We may use cookies and similar tracking technologies (like web beacons and pixels) to access or store information. Specific information about how we use such technologies and how you can refuse certain cookies is set out in our Cookie Policy.
            </Typography>

            <br/>

            <Typography variant="h5" gutterBottom>
                6. HOW LONG DO WE KEEP YOUR INFORMATION?
            </Typography>
            <Typography variant="body1" gutterBottom>
                <i>In Short: We keep your information for as long as necessary to fulfill the purposes outlined in this privacy policy unless otherwise required by law.</i>
            </Typography>
            <br/>
            <Typography variant="body1" gutterBottom>
                We will only keep your personal information for as long as it is necessary for the purposes set out in this privacy policy, unless a longer retention period is required or permitted by law. When we have no ongoing legitimate business need to process your personal information, we will either delete or anonymize it, or securely store it and isolate it from any further processing until deletion is possible.
            </Typography>

            <br/>

            <Typography variant="h5" gutterBottom>
                7. WHAT ARE YOUR PRIVACY RIGHTS?
            </Typography>
            <Typography variant="body1" gutterBottom>
                <i>In Short: You may review, change, or request deletion of your account data at any time.</i>
            </Typography>
            <br/>
            <Typography variant="body1" gutterBottom>
                If you have questions or comments about your privacy rights, you may email us at support@thewarriortribe.xyz.
            </Typography>
            <br/>
            <Typography variant="body1" gutterBottom>
                <strong>Account Information:</strong> If you would like to review, change, or delete the information in your account, contact us at support@thewarriortribe.xyz. Upon your request to terminate your account, we will deactivate or delete your account and information from our active databases. Some information may be retained in our files to prevent fraud, troubleshoot problems, enforce our Terms of Use, or comply with legal requirements.
            </Typography>

            <br/>

            <Typography variant="h5" gutterBottom>
                8. HOW CAN YOU CONTACT US ABOUT THIS POLICY?
            </Typography>
            <Typography variant="body1" gutterBottom>
                If you have questions or comments about this policy, you may email us at support@thewarriortribe.xyz.
            </Typography>

            <br/>

            <Typography variant="h5" gutterBottom>
                9. HOW CAN YOU REVIEW, UPDATE, OR DELETE THE DATA WE COLLECT FROM YOU?
            </Typography>
            <Typography variant="body1" gutterBottom>
                Based on the laws of some countries, you may have the right to request access to the personal information we collect from you, change that information, or delete it. To request to review, update, or delete your personal information, email us at support@thewarriortribe.xyz. We will respond to your request within 30 days.
            </Typography>

            <br/>

            <Typography variant="h5" gutterBottom>
                10. DO WE MAKE UPDATES TO THIS POLICY?
            </Typography>
            <Typography variant="body1" gutterBottom>
                <i>In Short: Yes, we will update this policy as necessary to stay compliant with relevant laws.</i>
            </Typography>
            <br/>
            <Typography variant="body1" gutterBottom>
                We may update this privacy policy from time to time. The updated version will be indicated by an updated "Last updated" date and will be effective as soon as it is accessible. We encourage you to review this privacy policy frequently to be informed of how we are protecting your information.
            </Typography>

            <br/>






      </div>
    );
  }